/**
 * @name useDependentesData
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Hook database-driven para gestão de dependentes usando views e functions do Supabase
 * @package components/pages/gestao-financeira/dependentes
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAlert, useLoading, useUserContext } from '../../../../contexts';
import { PessoasService } from '../../../../services/supabase/entities/PessoasService';
import { dependentesFunctionsService } from '../../../../services/supabase/functions/DependentesFunctionsService';
import { viewRelatorioConsumoAluno } from '../../../../services/supabase/views/ViewRelatorioConsumoAluno';
import { viewSaldosAlunos } from '../../../../services/supabase/views/ViewSaldosAlunos';
import { viewSaldosResponsaveis } from '../../../../services/supabase/views/ViewSaldosResponsaveis';
import { IPessoas } from '../../../../types/tables/IPessoas';
import { IVwRelatorioConsumoAluno } from '../../../../types/views/IVwRelatorioConsumoAluno';
import { IVwSaldosAlunos } from '../../../../types/views/IVwSaldosAlunos';

export interface DependenteComSaldo extends IVwSaldosAlunos {
     podeDistribuir: boolean;
     statusSaldo: 'alto' | 'medio' | 'baixo' | 'zerado';
     ultimaAtividade?: Date;
     saldoResponsavel?: number; // Adicionar saldo do responsável separadamente
}

export interface DistribuirSaldoData {
     idAluno: number;
     valor: number;
     observacoes?: string;
}

export interface TransferirSaldoData {
     idAlunoOrigem: number;
     idAlunoDestino: number;
     valor: number;
     observacoes?: string;
}

/**
 * Hook para gestão de dependentes usando views otimizadas do Supabase
 */
const useDependentesData = () => {
     const { userData } = useUserContext();
     const { showSuccess, showError } = useAlert();
     const { withGlobalLoading, loadingStates, setPageLoading } = useLoading();

     // Estados principais
     const [dependentes, setDependentes] = useState<DependenteComSaldo[]>([]);
     const [refreshing, setRefreshing] = useState(false);

     // Refs para controle de carregamento
     const isLoadingRef = useRef(false);
     const hasLoadedRef = useRef(false);

     // Helper para verificar estado de loading
     const isLoading = loadingStates['page-loading'] || false;

     // Estados para detalhes
     const [dependenteSelecionado, setDependenteSelecionado] = useState<DependenteComSaldo | null>(null);
     const [historicoCompras, setHistoricoCompras] = useState<IVwRelatorioConsumoAluno[]>([]);

     /**
      * Carrega dependentes do responsável logado usando abordagem híbrida
      */
     const carregarDependentes = useCallback(async () => {
          try {
               if (!userData?.id || !userData?.fk_id_organizacao) {
                    console.warn('Dados do usuário incompletos');
                    setDependentes([]);
                    return;
               }

               // Buscar saldo do responsável logado
               const saldoResponsavelResponse = await viewSaldosResponsaveis.getSaldoPorResponsavel(userData.fk_id_organizacao, userData.id);
               
               if (saldoResponsavelResponse.error) {
                    console.warn('Erro ao carregar saldo do responsável:', saldoResponsavelResponse.error.message);
               }

               const saldoResponsavel = saldoResponsavelResponse.data?.[0]?.saldo_atual || 0;
               console.log('Saldo do responsável encontrado:', saldoResponsavel); // Debug

               // Buscar saldos dos alunos dependentes
               const saldosResponse = await viewSaldosAlunos.getAlunosPorResponsavel(userData.fk_id_organizacao, userData.id);
               
               if (saldosResponse.error) {
                    console.warn('Erro ao carregar saldos dos dependentes:', saldosResponse.error.message);
               }

               const dadosSaldos = Array.isArray(saldosResponse.data) ? saldosResponse.data : [];
               console.log('Dados de saldos dos dependentes encontrados:', dadosSaldos); // Debug

               // Buscar dependentes direto da tabela de relacionamento
               const pessoasService = new PessoasService();
               const dependentesResponse = await pessoasService.getAlunosByResponsavel(userData.fk_id_organizacao, userData.id);
               
               if (dependentesResponse.error) {
                    showError('Erro ao carregar dependentes: ' + dependentesResponse.error.message);
                    return;
               }

               const relacionamentos = Array.isArray(dependentesResponse.data) ? dependentesResponse.data : [];
               console.log('Relacionamentos dependentes encontrados:', relacionamentos); // Debug

               if (relacionamentos.length === 0) {
                    console.log('Nenhum dependente encontrado para o responsável:', userData.id);
                    setDependentes([]);
                    return;
               }

               // Buscar dados completos dos alunos dependentes
               const idsAlunos = relacionamentos.map((rel: any) => rel.fk_id_aluno);
               
               const alunosCompletos: IPessoas[] = [];
               for (const idAluno of idsAlunos) {
                    console.log(`Buscando aluno ID: ${idAluno} na organização: ${userData.fk_id_organizacao}`); // Debug
                    const alunoResponse = await pessoasService.getById(idAluno, userData.fk_id_organizacao);
                    if (alunoResponse.data && !alunoResponse.error) {
                         const alunoData = alunoResponse.data as unknown as IPessoas;
                         alunosCompletos.push(alunoData);
                    }
               }

               console.log('Dados completos dos alunos dependentes:', alunosCompletos); // Debug

               // Combinar dados: relacionamento + dados do aluno + saldo do aluno + saldo do responsável
               const dependentesProcessados = alunosCompletos.map((aluno: IPessoas) => {
                    // Buscar saldo deste aluno específico na view de saldos
                    const saldoInfo = dadosSaldos.find((saldo: any) => saldo.id_aluno === aluno.id);
                    const saldoDisponivel = saldoInfo ? (saldoInfo as any).saldo_disponivel || 0 : 0;

                    return {
                         // Dados principais do dependente (compatível com IVwSaldosAlunos)
                         id_aluno: aluno.id,
                         aluno: aluno.nome || 'Nome não informado',
                         email: aluno.email || null,
                         id_responsavel: userData.id,
                         responsavel: userData.nome || null,
                         organizacao: null,
                         saldo_atual: saldoDisponivel, // Saldo do aluno/dependente
                         limite_diario: saldoInfo ? (saldoInfo as any).limite_diario || null : null,
                         limite_semanal: saldoInfo ? (saldoInfo as any).limite_semanal || null : null,
                         saldo_disponivel: saldoDisponivel, // Saldo do aluno/dependente
                         saldoResponsavel: saldoResponsavel, // Saldo do responsável
                         
                         // Campos adicionais processados baseados no saldo do dependente
                         podeDistribuir: saldoDisponivel < 50,
                         statusSaldo: (() => {
                              if (saldoDisponivel === 0) return 'zerado';
                              if (saldoDisponivel < 5) return 'baixo';
                              if (saldoDisponivel < 20) return 'medio';
                              return 'alto';
                         })() as 'alto' | 'medio' | 'baixo' | 'zerado',
                         ultimaAtividade: undefined,
                    };
               });

               console.log('Dependentes processados:', dependentesProcessados); // Debug
               setDependentes(dependentesProcessados);
               
          } catch (error: any) {
               console.error('Erro ao carregar dependentes:', error);
               showError('Erro inesperado ao carregar dependentes: ' + error.message);
          }
     }, [userData?.id, userData?.fk_id_organizacao, userData?.nome, userData?.email, showError]);

     /**
      * Distribui saldo para um dependente usando fn_transferir_credito_para_aluno
      */
     const distribuirSaldo = useCallback(
          async (dados: DistribuirSaldoData): Promise<boolean> => {
               if (!userData?.fk_id_organizacao) {
                    showError('Dados do usuário não encontrados');
                    return false;
               }

               return withGlobalLoading(async () => {
                    try {
                         const response = await dependentesFunctionsService.distribuirSaldoDependente(userData?.id!, dados.idAluno, userData.fk_id_organizacao!, dados.valor, dados.observacoes);

                         if (response.error) {
                              showError('Erro ao distribuir saldo: ' + response.error.message);
                              return false;
                         }

                         // Processar resposta da function
                         const result = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

                         if (result.success) {
                              showSuccess(`Saldo de R$ ${dados.valor.toFixed(2)} transferido com sucesso!`);
                              // Recarregar dados após sucesso
                              await carregarDependentes();
                              return true;
                         } else {
                              showError(result.message || 'Erro ao distribuir saldo');
                              return false;
                         }
                    } catch (error: any) {
                         console.error('Erro ao distribuir saldo:', error);
                         showError('Erro inesperado ao distribuir saldo: ' + error.message);
                         return false;
                    }
               });
          },
          [userData?.id, userData?.fk_id_organizacao, withGlobalLoading, showSuccess, showError],
     );

     /**
      * Transfere saldo entre dependentes usando fn_transferir_entre_dependentes
      */
     const transferirEntreDependentes = useCallback(
          async (dados: TransferirSaldoData): Promise<boolean> => {
               return withGlobalLoading(async () => {
                    try {
                         const response = await dependentesFunctionsService.transferirEntreDependentes(
                              userData?.id!,
                              dados.idAlunoOrigem,
                              dados.idAlunoDestino,
                              userData?.fk_id_organizacao!,
                              dados.valor,
                              dados.observacoes
                         );

                         if (response.error) {
                              showError('Erro ao transferir saldo: ' + response.error.message);
                              return false;
                         }

                         // Processar resposta da function
                         const result = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

                         if (result.success) {
                              showSuccess(`Transferência de R$ ${dados.valor.toFixed(2)} realizada com sucesso!`);
                              // Pequeno delay para garantir atualização da view no banco
                              await new Promise((resolve) => setTimeout(resolve, 600));
                              await carregarDependentes();
                              return true;
                         } else {
                              showError(result.message || 'Erro ao transferir saldo');
                              return false;
                         }
                    } catch (error: any) {
                         console.error('Erro ao transferir saldo:', error);
                         showError('Erro inesperado ao transferir saldo: ' + error.message);
                         return false;
                    }
               });
          },
          [userData?.id, userData?.fk_id_organizacao, withGlobalLoading, showSuccess, showError],
     );

     /**
      * Carrega histórico de compras de um dependente específico
      */
     const carregarHistoricoCompras = useCallback(
          async (idAluno: number) => {
               if (!userData?.fk_id_organizacao) return;

               setRefreshing(true);
               try {
                    const response = await viewRelatorioConsumoAluno.getConsumoPorAluno(userData.fk_id_organizacao, idAluno);

                    if (response.error) {
                         showError('Erro ao carregar histórico: ' + response.error.message);
                         return;
                    }

                    setHistoricoCompras(response.data || []);
               } catch (error: any) {
                    showError('Erro inesperado ao carregar histórico: ' + error.message);
               } finally {
                    setRefreshing(false);
               }
          },
          [userData?.fk_id_organizacao, showError],
     );

     /**
      * Seleciona um dependente e carrega seus detalhes
      */
     const selecionarDependente = useCallback(
          async (dependente: DependenteComSaldo) => {
               setDependenteSelecionado(dependente);
               if (dependente.id_aluno) {
                    await carregarHistoricoCompras(dependente.id_aluno);
               }
          },
          [carregarHistoricoCompras],
     );

     /**
      * Atualiza dados sem loading global
      */
     const refresh = useCallback(async () => {
          setRefreshing(true);
          try {
               await carregarDependentes();
          } catch (error: any) {
               showError('Erro inesperado ao recarregar dependentes: ' + error.message);
          } finally {
               setRefreshing(false);
          }
     }, [carregarDependentes, showError]);

     /**
      * Configura restrições alimentares (função futura)
      */
     const configurarRestricoes = useCallback(
          async (_idAluno: number, _restricoes: any[]): Promise<boolean> => {
               // TODO: Implementar quando a function fn_configurar_restricoes_aluno estiver criada
               showError('Funcionalidade de configuração de restrições em desenvolvimento');
               return false;
          },
          [showError],
     );

     // Métricas calculadas
     const metricas = {
          totalDependentes: dependentes.length,
          totalSaldoDistribuido: dependentes.length > 0 ? (dependentes[0]?.saldoResponsavel || 0) : 0, // Saldo do responsável
          dependentesComSaldoBaixo: dependentes.filter((dep) => dep.statusSaldo === 'baixo' || dep.statusSaldo === 'zerado').length,
          dependentesAtivos: dependentes.filter((dep) => (dep.saldo_disponivel || 0) > 0).length,
     };

     // Carregamento inicial
     useEffect(() => {
          if (userData?.id && userData?.fk_id_organizacao && !hasLoadedRef.current && !isLoadingRef.current) {
               console.log('✅ [useDependentesData] Iniciando carregamento inicial...');
               hasLoadedRef.current = true;
               isLoadingRef.current = true;
               setPageLoading(true);
               
               carregarDependentes()
                    .finally(() => {
                         setPageLoading(false);
                         isLoadingRef.current = false;
                    });
          }
     }, [userData?.id, userData?.fk_id_organizacao, carregarDependentes, setPageLoading]);

     return {
          // Estados
          dependentes,
          dependenteSelecionado,
          historicoCompras,
          loading: isLoading, // ✅ Usando LoadingContext
          refreshing,
          metricas,

          // Ações
          carregarDependentes,
          distribuirSaldo,
          transferirEntreDependentes,
          selecionarDependente,
          configurarRestricoes,
          refresh,

          // Context info
          organizationId: userData?.fk_id_organizacao!,
          responsavelId: userData?.id!,
          isResponsavel: userData?.fk_id_tipo_pessoa === 4, // Tipo responsável

          // Metadados
          metadados: {
               titulo: 'Meus Dependentes',
               subtitulo: 'Gerencie saldos e acompanhe atividades dos seus dependentes',
               paginaTitulo: 'Meus Dependentes',
               breadcrumbs: [
                    { label: 'Início', path: '/' },
                    { label: 'Gestão Financeira', path: '/gestao-financeira' },
                    { label: 'Dependentes', path: '/gestao-financeira/dependentes' },
               ],
          },
     };
};

export default useDependentesData;
